import NextLink from 'next/link';
import React, { useState } from 'react';
import ReactGA from 'react-ga4';
import PropTypes from 'prop-types';
import { Typography, Link } from '@mui/material';
import DropdownButton from '../DropdownButton';
import OldCreationVersionModal from '../OldCreationVersionModal';
import CardMedia from './components/CardMedia';
import { useStyles } from './styles';
import { blackNovemberPromotionData, getEnv, isAnniversaryActive } from 'utils';
import BlackNovemberLimitModal from '../BlackNovemberLimitModal';
import AnniversaryMindTheGraphModal from '../AnniversaryMindTheGraphModal';
import LimitationModal from '../LimitationModal';
import { useRouter } from 'next/router';
import getCleverTap from 'utils/clevertap';
import { useAuthUser } from 'features/auth/AuthUserContext';

const Props = {
  creation: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    deleted: PropTypes.number.isRequired,
    thumb: PropTypes.string.isRequired,
    slide_id: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    new_version: PropTypes.number.isRequired,
  }).isRequired,
  blockedAccess: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onOptionClick: PropTypes.func.isRequired,
};

export default function CreationCard({ creation, blockedAccess, options, onOptionClick }) {
  const classes = useStyles();
  const { user } = useAuthUser();
  const router = useRouter();
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [oldVersionModalOpen, setOldVersionModalOpen] = useState(false);
  const oldWorkspaceUrl = `${getEnv('MTG_URL_LEGACY')}/workspace/user-creations/${creation.id}`;
  const promotionBlacknovember = blackNovemberPromotionData();
  const clevertap = getCleverTap();

  function handleOldVersionModalClose() {
    setOldVersionModalOpen(false);
  }

  function handleLinkAccess(event) {
    ReactGA.event({
      category: 'workspace',
      action: 'access_existing_creation',
      label: 'Click on any creation in My Creations',
      value: user.id,
    });

    clevertap.event.push('View Project', {
      Product: 'MTG',
      Email: user.email,
      'Country code': user.country_code,
      URL: router.asPath,
      Description: 'Access Existing Creation',
      Locale: 'my-creations',
    });

    if (blockedAccess) {
      event.preventDefault();

      setLimitModalOpen(true);
    }

    if (user.useNewWorkspace && !creation.new_version) {
      event.preventDefault();

      setOldVersionModalOpen(true);
    }
  }

  function resolveCreationLinkComponent(label) {
    if (!creation.new_version) {
      return (
        <Link
          href={!user.useNewWorkspace ? oldWorkspaceUrl : undefined}
          onClick={handleLinkAccess}
          underline="hover"
        >
          {label}
        </Link>
      );
    }

    return (
      <NextLink href={`/workspace/${creation.id}`} passHref={true}>
        <Link onClick={handleLinkAccess} underline="hover">
          {label}
        </Link>
      </NextLink>
    );
  }

  return (
    <>
      {promotionBlacknovember.isActive && (
        <BlackNovemberLimitModal open={limitModalOpen} onClose={() => setLimitModalOpen(false)} />
      )}

      {isAnniversaryActive() && (
        <AnniversaryMindTheGraphModal
          open={limitModalOpen}
          onClose={() => setLimitModalOpen(false)}
        />
      )}

      {!promotionBlacknovember.isActive && !isAnniversaryActive() && (
        <LimitationModal open={limitModalOpen} onClose={() => setLimitModalOpen(false)} />
      )}

      <OldCreationVersionModal
        open={oldVersionModalOpen}
        onClose={handleOldVersionModalClose}
        creation={creation}
      />

      <div className={classes.card}>
        {!creation.deleted ? (
          resolveCreationLinkComponent(
            <CardMedia creation={creation} blockedAccess={blockedAccess} />,
          )
        ) : (
          <CardMedia creation={creation} blockedAccess={blockedAccess} />
        )}

        <div>
          <Typography className={classes.cardText} variant="body2" color="textSecondary">
            {!creation.deleted ? resolveCreationLinkComponent(creation.name) : creation.name}
          </Typography>

          {!!options.length && <DropdownButton options={options} onOptionClick={onOptionClick} />}
        </div>
      </div>
    </>
  );
}

CreationCard.propTypes = Props;
